/**
 * Nutrition Module Constants
 * Centralized configuration for the nutrition tracking system
 */

// LocalStorage Keys
export const STORAGE_KEYS = {
  NUTRITION: 'gym_notebook_nutrition',
  NUTRITION_GOALS: 'gym_notebook_nutrition_goals',
  SAVED_BARCODES: 'gym_notebook_saved_barcodes',
  SAVED_MEALS: 'gym_notebook_saved_meals',
  CUSTOM_MEAL_TIMES: 'gym_notebook_custom_meal_times',
  EXTERNAL_STEPS: 'gym_notebook_external_steps',
  CUSTOM_PRODUCTS: 'gym_notebook_custom_products',
  BURNED_CALORIES: 'gym_notebook_burned_calories',
  ONBOARDING_COMPLETE: 'nutrition_onboarding_complete',
  BODY_WEIGHT: 'gym_notebook_persistent_bodyweight',
  BMR_SETTINGS: 'gym_notebook_bmr_settings',
  SAVED_MENUS: 'gym_notebook_saved_menus',
} as const;

// Macro goals interface
export interface MacroGoals {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
}

// Default macro goals
export const DEFAULT_GOALS: MacroGoals = {
  calories: 2000,
  protein: 150,
  carbs: 200,
  fat: 65,
};

// Meal time interface
export interface MealTime {
  id: string;
  name: string;
  icon?: string;
  isCustom?: boolean;
}

// Default meal times
export const DEFAULT_MEAL_TIMES: MealTime[] = [
  { id: 'breakfast', name: 'Breakfast', icon: '🌅' },
  { id: 'lunch', name: 'Lunch', icon: '☀️' },
  { id: 'dinner', name: 'Dinner', icon: '🌙' },
  { id: 'snack', name: 'Snack', icon: '🍎' },
];

// Macro color scheme interface
export interface MacroColorScheme {
  bg: string;
  text: string;
  border: string;
  progress: string;
}

// Macro colors for UI
export const MACRO_COLORS: Record<string, MacroColorScheme> = {
  calories: {
    bg: 'bg-orange-500/20',
    text: 'text-orange-400',
    border: 'border-orange-500/30',
    progress: 'bg-orange-500',
  },
  protein: {
    bg: 'bg-red-500/20',
    text: 'text-red-400',
    border: 'border-red-500/30',
    progress: 'bg-red-500',
  },
  carbs: {
    bg: 'bg-yellow-500/20',
    text: 'text-yellow-400',
    border: 'border-yellow-500/30',
    progress: 'bg-yellow-500',
  },
  fat: {
    bg: 'bg-blue-500/20',
    text: 'text-blue-400',
    border: 'border-blue-500/30',
    progress: 'bg-blue-500',
  },
};

// Open Food Facts API - .net as primary (more reliable), .org as fallback
export const OPEN_FOOD_FACTS_API = 'https://world.openfoodfacts.net';
export const OPEN_FOOD_FACTS_API_FALLBACK = 'https://world.openfoodfacts.org';

/** Fetch from OpenFoodFacts with automatic .net → .org fallback */
export async function fetchOpenFoodFacts(path: string, signal?: AbortSignal): Promise<Response> {
  try {
    const res = await fetch(`${OPEN_FOOD_FACTS_API}${path}`, { signal });
    if (res.ok) return res;
  } catch { /* noop */ }
  return fetch(`${OPEN_FOOD_FACTS_API_FALLBACK}${path}`, { signal });
}

// Activity multipliers for TDEE calculation
export const ACTIVITY_MULTIPLIERS: Record<string, number> = {
  sedentary: 1.2,      // Little or no exercise
  light: 1.375,        // Light exercise 1-3 days/week
  moderate: 1.55,      // Moderate exercise 3-5 days/week
  active: 1.725,       // Hard exercise 6-7 days/week
  veryActive: 1.9,     // Very hard exercise & physical job
};

// Calories per step (average for 70kg person)
export const CALORIES_PER_STEP_BASE = 0.04;
export const BASE_WEIGHT_KG = 70;

// Fresh produce category interface
export interface FreshProduceCategory {
  id: string;
  name: string;
}

// Fresh produce categories
export const FRESH_PRODUCE_CATEGORIES: FreshProduceCategory[] = [
  { id: 'all', name: 'All' },
  { id: 'fruits', name: 'Fruits' },
  { id: 'vegetables', name: 'Vegetables' },
  { id: 'herbs', name: 'Herbs' },
];

// Copy target option interface
export interface CopyTargetOption {
  id: string;
  label: string;
}

// Copy target options
export const COPY_TARGET_OPTIONS: CopyTargetOption[] = [
  { id: 'today', label: 'Today' },
  { id: 'tomorrow', label: 'Tomorrow' },
  { id: 'yesterday', label: 'Yesterday' },
];
